
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import type { SecurityEvent, ComplianceViolation } from '@/hooks/useSecurityCompliance';

interface SecurityMetricsCardsProps {
  securityEvents: SecurityEvent[];
  violations: ComplianceViolation[];
}

const SecurityMetricsCards = ({ securityEvents, violations }: SecurityMetricsCardsProps) => {
  const severityCounts = securityEvents.reduce((acc, event) => {
    acc[event.severity] = (acc[event.severity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const openViolationsCount = violations.filter(v => v.status === 'open').length;
  const resolvedTodayCount = violations.filter(v => 
    v.status === 'resolved' && 
    v.resolved_at && 
    new Date(v.resolved_at).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Events</CardTitle>
          <Shield className="h-4 w-4 text-black" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{securityEvents.length}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Critical Events</CardTitle>
          <AlertTriangle className="h-4 w-4 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">
            {severityCounts.critical || 0}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Open Violations</CardTitle>
          <Clock className="h-4 w-4 text-sky-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-black">
            {openViolationsCount}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Resolved Today</CardTitle>
          <CheckCircle className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            {resolvedTodayCount}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityMetricsCards;
